/* eslint-disable unicorn/filename-case */
let cached: ServiceWorkerRegistration | null = null;

async function getSwState(): Promise<string | undefined> {
  const registration = await navigator.serviceWorker.getRegistration();
  return registration?.active?.state;
}

export async function registerAppSW(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;

  let refreshing = false;
  const oldSwState = await getSwState();

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;

    void getSwState().then((newSwState) => {
      if (oldSwState === 'activated' && newSwState === 'activating') {
        refreshing = true;
        globalThis.location.reload();
      }
    });
  });

  try {
    cached = await (import.meta.env.DEV
      ? navigator.serviceWorker.register('./dev-sw.js?dev-sw', { type: 'module' })
      : navigator.serviceWorker.register('./sw.js', { type: 'module' }));
    scheduleUpdateChecks(cached);
    return cached;
  } catch {
    return null;
  }
}

/**
 * The browser only looks for a new `sw.js` on a navigation within scope or an
 * explicit `update()`. Registration above is the only check this app made, so a
 * tab that is never reloaded never learned a new version existed -- and because
 * the worker serves navigations from the precache
 * (`NavigationRoute(createHandlerBoundToURL('index.html'))`), it kept handing
 * back its own cached shell indefinitely.
 *
 * That is not academic. `VITE_API_URL` is inlined into the bundle at build time,
 * so the API a session talks to is fixed by whichever bundle it is running. When
 * the API moved to a new gateway on 2026-09-15, traffic to the old one fell away
 * over three days and then flattened at a floor that never cleared -- parked
 * sessions, mostly mobile, still calling the gateway we intend to decommission.
 *
 * `skipWaiting()` and `clients.claim()` in the worker, plus the
 * `controllerchange` reload above, already make an update apply promptly once it
 * is *found*. Finding it is the missing half.
 */
function scheduleUpdateChecks(registration: ServiceWorkerRegistration): void {
  const check = (): void => {
    void registration.update().catch(() => {
      // Deliberately swallowed, and not sent to captureError like the rest of
      // the app: a rejected update() is routine rather than a fault -- offline,
      // or the browser declining to re-request sw.js while it still considers
      // it fresh. The next check will try again.
    });
  };

  // Catches the case the interval cannot: a tab backgrounded for days on a
  // phone, where timers are throttled or suspended entirely, then brought back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });

  // Hourly, for a tab left open and visible.
  globalThis.setInterval(check, 60 * 60 * 1000);
}

export async function getAppSW(): Promise<ServiceWorkerRegistration | null> {
  if (cached) return cached;
  if (!('serviceWorker' in navigator)) return null;
  cached = await navigator.serviceWorker.ready;
  return cached;
}
